import React from 'react';
import { Typography } from 'antd';
import useTranslation from '../../hooks/useTranslation';
import useSelectedDeck from '../../hooks/useSelectedDeck';
import useDeckLearningMethod from '../../hooks/useDeckLearningMethod';
import DeckItemsTable from '../../components/Practice/DeckItemsTable';
import { QUIZ_PRACTICE_TYPE } from '../../constants/quizPractice';
import { CARD_PRACTICE_TYPE } from '../../constants/cardPractice';
import { WORD_PRACTICE_TYPE } from '../../constants/wordPractice';
import './ProgressPage.css';

const { Title, Text } = Typography;

/** Тип практики по типу колоды — тот же, что шлют страницы обучения в init. */
const PRACTICE_TYPES = {
    QUIZ: QUIZ_PRACTICE_TYPE,
    CARD: CARD_PRACTICE_TYPE,
    WORD: WORD_PRACTICE_TYPE,
};

/**
 * Прогресс по колоде: каждый элемент с его состоянием памяти, в порядке любого столбца
 * и под фильтрами. Своё место, а не кнопка под панелью методики: список сравним по
 * размеру с самой колодой, и на странице обучения он вытеснял выбор практики. Панель
 * методики там осталась картиной и переключателем; здесь — построчно.
 *
 * Столбцы — под выбранную методику колоды: состояние одно, но лестница спрашивает про
 * коробку, адаптивная — про стабильность, критерий — про число верных.
 */
const ProgressPage = () => {
    const { t } = useTranslation();
    const selectedDeck = useSelectedDeck();
    const deckId = selectedDeck?.deckId ?? null;
    const practiceType = selectedDeck ? PRACTICE_TYPES[selectedDeck.type] : null;
    const { stats, learningMethod, isLoading } =
        useDeckLearningMethod(deckId, practiceType, t('practice.method.loadFailed'));

    const methodLabel = learningMethod ? t('practice.method.' + methodKey(learningMethod)) : null;

    if (!selectedDeck) {
        return <div className="progress__empty">{t('progress.selectDeck')}</div>;
    }
    if (!practiceType) {
        return <div className="progress__empty">{t('deck.selectValidType')}</div>;
    }

    return (
        <div className="progress">
            <div className="progress__head">
                <Title level={4} className="progress__title">
                    {t('progress.title')}
                    <Text type="secondary" className="progress__deck"> · {selectedDeck.title}</Text>
                </Title>
                {stats && (
                    <Text type="secondary" className="progress__counts">
                        {t('progress.counts', { tracked: stats.trackedCount, count: stats.itemCount })}
                        {methodLabel && ` · ${methodLabel}`}
                    </Text>
                )}
            </div>
            {/* Таблица ждёт методику: без неё неизвестно, какие столбцы показывать, а
                нарисовать лестницу и заменить её через полсекунды хуже, чем подождать. */}
            {learningMethod && (
                <DeckItemsTable
                    deckId={deckId}
                    practiceType={practiceType}
                    learningMethod={learningMethod}
                    boxCount={stats?.reviewIntervalDays?.length ?? 0}
                />
            )}
            {!learningMethod && isLoading && (
                <div className="progress__empty">{t('practice.run.loading')}</div>
            )}
        </div>
    );
};

const methodKey = (learningMethod) => ({
    FIXED_SCHEDULE: 'fixedSchedule',
    ADAPTIVE_SCHEDULE: 'adaptiveSchedule',
    MASTERY_RULE: 'masteryRule',
}[learningMethod] ?? 'fixedSchedule');

export default ProgressPage;
